<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="fr">
        <section class="intro">
          <p>
            Partager des photos 360° sur les réseaux sociaux touche une audience
            bien plus large que la seule publication sur votre site. Mais chaque
            plateforme gère le contenu 360° différemment — Facebook l'affiche de
            manière interactive, Instagram ne prend pas en charge le 360° natif,
            YouTube ne gère que la vidéo 360°. Comprendre ces nuances détermine
            si votre contenu 360° atteint des milliers de personnes ou seulement
            une poignée.
          </p>
        </section>

        <section>
          <h2>Le support 360° plateforme par plateforme</h2>

          <h3>Facebook : support natif complet</h3>
          <p>
            Facebook est la meilleure plateforme pour partager des photos 360°.
            Lorsque vous téléversez un JPEG équirectangulaire avec les
            métadonnées XMP Photo Sphere correctes, Facebook détecte
            automatiquement le format 360° et l'affiche comme un visionneur
            sphérique interactif. Les utilisateurs peuvent pivoter, incliner et
            zoomer directement dans leur fil d'actualité sur ordinateur et
            mobile.
          </p>
          <p>
            <strong>Comment publier en 360° sur Facebook :</strong> téléversez
            le fichier source équirectangulaire directement comme photo (pas via
            un lien). Ne partagez pas via URL — Facebook n'active le visionneur
            360° que pour les images téléversées directement. Les fichiers
            exportés depuis Travvir, Insta360, Ricoh Theta et l'application
            Google Street View contiennent ces métadonnées automatiquement.
          </p>
          <p>
            <strong>Impact sur l'engagement :</strong> les photos 360° sur
            Facebook obtiennent en moyenne
            <strong>3× plus d'interactions</strong> que les photos standard et
            <strong>4× plus de partages</strong>.
          </p>

          <h3>Instagram : pas de support 360° natif</h3>
          <p>
            Instagram n'affiche pas les photos 360° de manière interactive. Si
            vous uploadez une image équirectangulaire, elle apparaît comme un
            rectangle plat et fortement déformé.
          </p>
          <p>
            <strong>Meilleure approche pour Instagram :</strong> exportez un
            recadrage plat "aperçu" depuis votre image 360° — un grand-angle ou
            une vue double qui montre la perspective la plus intéressante. Dans
            la légende, dirigez vos abonnés vers le tour interactif complet via
            un lien (en bio ou autocollant de lien dans les Stories).
            Alternatively, enregistrez une courte vidéo de balayage à travers le
            tour 360° et publiez-la en Reel.
          </p>

          <h3>YouTube : vidéo 360° seulement</h3>
          <p>
            YouTube prend en charge la vidéo 360° avec une visualisation
            interactive complète, mais pas les photos fixes 360°. Pour partager
            du contenu 360° sur YouTube, créez une vidéo 360° — soit en faisant
            un panoramique filmé, soit en convertissant vos scènes en vidéo de
            type diaporama.
          </p>

          <h3>Google Maps / Street View</h3>
          <p>
            Google Maps prend en charge la publication directe de photos 360°
            sur Street View. Utilisez l'application Google Street View pour
            publier vos équirectangulaires. Ces photos apparaissent dans Street
            View, dans votre Profil Google Business, et peuvent améliorer votre
            référencement local.
          </p>
        </section>

        <section>
          <h2>Checklist avant de partager une photo 360°</h2>
          <ul>
            <li>Fichier JPEG équirectangulaire avec rapport 2:1</li>
            <li>
              Métadonnées XMP Photo Sphere présentes (vérifiez avec Exif Viewer)
            </li>
            <li>Résolution minimale de 4 096 × 2 048 px pour Facebook</li>
            <li>Taille de fichier inférieure à 30 Mo pour Facebook</li>
            <li>Téléversement direct comme photo, jamais via lien</li>
          </ul>
        </section>

        <section>
          <h2>Questions fréquentes</h2>

          <h3>
            Pourquoi ma photo 360° ne s'affiche-t-elle pas en mode interactif
            sur Facebook ?
          </h3>
          <p>
            Vérifiez que les métadonnées XMP Photo Sphere sont bien présentes
            dans votre fichier et que vous avez téléversé l'image directement
            (pas via un lien). Les images redimensionnées ou retraitées perdent
            parfois ces métadonnées.
          </p>

          <h3>
            Peut-on partager un tour virtuel complet (multi-scènes) sur les
            réseaux sociaux ?
          </h3>
          <p>
            Pas directement. Partagez le lien vers votre tour Travvir dans votre
            publication, ou utilisez une photo 360° unique comme accroche
            visuelle pour diriger les abonnés vers l'expérience complète.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Continuez la Lecture</h2>
          <ul>
          <li><a href="/blog/fr/qu-est-ce-photo-360">Qu'est-ce qu'une Photo 360° ? Guide Complet 2026</a></li>
          <li><a href="/blog/fr/photos-360-avec-smartphone">Comment Prendre des Photos 360° avec un Smartphone : Guide Complet</a></li>
          <li><a href="/blog/fr/integrer-visite-virtuelle-site-web">Comment Intégrer une Visite Virtuelle 360° sur son Site Web 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-360-debutants">Visite Virtuelle 360° pour Débutants : Créez votre 1er Tour depuis le Téléphone</a></li>
          <li><a href="/blog/fr/analytics-visite-virtuelle">Analytics Visite Virtuelle : Métriques Clés et Optimisation</a></li>
          <li><a href="/blog/fr/visite-virtuelle-360-guide-complet">Visite Virtuelle 360° : Guide Complet pour Débutants (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "partager-photo-360-reseaux-sociaux",
);

useBlogSeo({
  title:
    "Comment Partager des Photos 360° sur les Réseaux Sociaux 2026 | Travvir",
  description:
    "Guide complet pour partager des photos 360° sur Facebook, Instagram, YouTube et Google Maps. Formats requis, métadonnées, astuces d'engagement.",
  keywords:
    "partager photo 360 réseaux sociaux, photo 360 facebook, photo 360 instagram, partager visite virtuelle réseaux sociaux",
  slug: "partager-photo-360-reseaux-sociaux",
  image: postData?.image,
  postData,
  lang: "fr",
  faqItems: [
    {
      question:
        "Pourquoi ma photo 360° ne s'affiche-t-elle pas en interactif sur Facebook ?",
      answer:
        "Vérifiez les métadonnées XMP Photo Sphere et uploadez l'image directement, pas via un lien.",
    },
    {
      question:
        "Peut-on partager un tour multi-scènes directement sur les réseaux ?",
      answer:
        "Non directement. Partagez le lien Travvir avec une photo 360° unique comme accroche.",
    },
  ],
});

</script>
